import React, { useState, useCallback } from 'react';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { HiHeaderComponent, ActivitiesSection, TodayReminders, DeviceIssueCardStack } from '../components';
import { ScrollView, RefreshControl } from 'react-native';
import { View } from '@shared/components/md-design-system/Layout/View';
import { useTourScrollView } from 'app/_modules/onboarding';
import { useFetch } from 'app/queryProvider';
import { IPathwayActivitiesResponse } from 'app/_modules/pathways/entities';
import { ActivityName } from '../types';
import { Loading } from '@shared/components/loading';
import { useAuth } from 'app/auth/hooks/useAuth';
import { useTranslation } from 'react-i18next';
import MainErrorWithIcon from '@shared/components/ModularInfoWithIcon';
import { IReportsResponse, TelemonitoringReportCard } from 'app/_modules/history';
import { Telemetry } from 'app/services/telemetry/Telemetry';
import { useWebSocketMeasurementService } from '@shared/hooks/useWebSocketMeasurementService';
import { useMergedActivities } from '@shared/hooks/useMergedActivities';
import { TDeviceIssueType } from 'app/types/deviceIssue';
import { useDeviceIssues } from '../hooks/useDeviceIssues';

const Home = () => {
    const { meData } = useAuth()
    const { t } = useTranslation();
    const navigation = useNavigation<NativeStackNavigationProp<any>>();
    const [refreshing, setRefreshing] = useState(false);
    const { measurements } = useWebSocketMeasurementService();
    const deviceIssues = useDeviceIssues();

    const todayFormattedDate = new Intl.DateTimeFormat('en-CA').format(new Date());

    const pathwayId = meData?.current_pathway? meData?.current_pathway.id : undefined ;

    const { data: pathwayActivities, isFetching: isFetchingPathwayActivities, isError: isActivitiesError, refetch: refetchActivities } = useFetch<IPathwayActivitiesResponse>({
        url: `/activities?date=${todayFormattedDate}`,
        queryKey: [["pathway", "activities"]],
        resource: { name: "pathways", id: pathwayId },
        headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
            'Expires': '0'
        },
        options: {
            enabled: !!pathwayId
        }
    });

    const { data: reportsResponse, isFetching: isFetchingReports, refetch: refetchReports } = useFetch<IReportsResponse>({
        url: '?_filter={"status":"sent", "opened_by_patient":false}',
        queryKey: [["telemonitoring-reports", "list", "not_opened_by_patient"]],
        resource: { name: "telemonitoring-reports" },
        options: {
            enabled: !!pathwayId
        }
    });

    const mergedActivities = useMergedActivities(pathwayActivities, measurements);

    const tourScroll = useTourScrollView();

    const stepsActivity = mergedActivities
        ? (['daily', 'morning', 'afternoon', 'evening'] as const)
            .flatMap((bucket) => mergedActivities[bucket] || [])
            .find((activity) => activity.activityName === ActivityName.STEP)
        : undefined;

    const hasActivities = !!mergedActivities && (
        (mergedActivities.daily?.length ?? 0) > 0 ||
        (mergedActivities.morning?.length ?? 0) > 0 ||
        (mergedActivities.afternoon?.length ?? 0) > 0 ||
        (mergedActivities.evening?.length ?? 0) > 0
    );

    const reports = reportsResponse?.results || [];

    const handleSelectDeviceIssue = useCallback((issueType: TDeviceIssueType) => {
        navigation.navigate('DeviceTroubleshooting', { issueType });
    }, [navigation]);

    const onRefresh = useCallback(async () => {
        setRefreshing(true);
        Telemetry.log('info', 'Refreshing home screen data...', { date: new Date().toLocaleString() });
        try {
            await Promise.all([
                refetchActivities(),
                refetchReports()
            ]);
        } finally {
            setRefreshing(false);
        }
    }, [refetchActivities, refetchReports]);


    if (isFetchingPathwayActivities || !pathwayId) {
        return <Loading message={t('commons.loading.pathway')} />
    }

    return (
        <View style={{ flex: 1 }}>
            <HiHeaderComponent stepsActivity={stepsActivity} />
            <ScrollView
                {...tourScroll}
                style={{ flex: 1 }}
                contentContainerStyle={{ paddingBottom: 32 }}
                refreshControl={
                    <RefreshControl
                        refreshing={refreshing}
                        onRefresh={onRefresh}
                    />
                }
            >
            <View variant="pagecontainer" style={{ paddingTop: 24 }}>
                <DeviceIssueCardStack
                    issues={deviceIssues}
                    onSelectIssue={handleSelectDeviceIssue}
                />
                {reports.map(report => (
                    <TelemonitoringReportCard
                        key={report.id}
                        openedByPatient={false}
                        reportData={{
                            id: report.id,
                            title: report.reportName,
                            startDate: report.periodStartDate,
                            endDate: report.periodEndDate,
                            receivedDate: report.createdAt,
                            status: report.status
                        }}
                    />
                ))}
                {isActivitiesError ? (
                    <MainErrorWithIcon errorType={'no_connection'} />
                ) : hasActivities ? (
                    <View className="flex-col gap-8">
                        <TodayReminders activities={mergedActivities!} />
                        <ActivitiesSection activities={mergedActivities!} />
                    </View>
                ) : (
                    <MainErrorWithIcon errorType={'no_activities'} />
                )}
            </View>
            </ScrollView>
        </View>
    );
};
export default Home;
